<template>
  <el-aside
    v-if="clientWidth > 1000"
    class="layout-aside"
    :class="[asideWidthClass, asideLayoutClass]"
  >
    <layout-logo v-if="showAsideLogo" class="layout-aside-logo" />
    <div v-if="catTitle" class="cat-name">{{ catTitle }}</div>
    <el-scrollbar class="flex-auto">
      <vertical-nav :menu-list="asideMenuList" />
    </el-scrollbar>
  </el-aside>
  <!-- 移动端：对齐 EB aside.vue，抽屉显隐绑定 themeConfig.isCollapse -->
  <el-drawer
    v-else
    v-model="mobileDrawerOpen"
    class="layout-aside-mobile-drawer-wrap"
    :with-header="false"
    direction="ltr"
    size="180px"
  >
    <el-aside
      class="layout-aside layout-aside-mobile-drawer w100 h100"
      :class="asideLayoutClass"
    >
      <layout-logo v-if="showAsideLogo" class="layout-aside-logo" />
      <el-scrollbar class="flex-auto">
        <vertical-nav :menu-list="asideMenuList" />
      </el-scrollbar>
    </el-aside>
  </el-drawer>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import useCrmebMenuStore from '@/store/modules/menu';
import useCrmebThemeConfigStore from '@/store/modules/themeConfig';
import LayoutLogo from '../logo/index.vue';
import VerticalNav from '../navMenu/Vertical.vue';

const crmebMenu = useCrmebMenuStore();
const themeStore = useCrmebThemeConfigStore();

const clientWidth = ref(typeof document !== 'undefined' ? document.body.clientWidth : 1200);

/** 对应 EB: el-drawer :visible.sync="getThemeConfig.isCollapse" */
const mobileDrawerOpen = computed({
  get: () => themeStore.themeConfig.isCollapse,
  set: (v: boolean) => themeStore.setCollapse(v),
});

const asideMenuList = computed(() => {
  const layout = themeStore.themeConfig.layout;
  if (layout === 'columns' || layout === 'classic') {
    return crmebMenu.childNavList;
  }
  return crmebMenu.columnsNavList;
});

/**
 * 对齐 EB aside.vue menuList getter：
 * childMenuList.length > 0 → isCollapse=false，否则 true。
 * 避免一级下仅一个二级时分栏侧栏 width:0（店员管理及三级不可见）
 */
watch(
  () => [themeStore.themeConfig.layout, crmebMenu.childNavList.length] as const,
  ([layout, childLen]) => {
    if (layout !== 'columns' && layout !== 'classic') return;
    themeStore.setCollapse(childLen > 0 ? false : true);
  },
  { immediate: true },
);

const showAsideLogo = computed(() => {
  const { layout, isShowLogo } = themeStore.themeConfig;
  return isShowLogo && layout === 'defaults' && asideMenuList.value.length > 0;
});

const catTitle = computed(() => {
  const layout = themeStore.themeConfig.layout;
  if (layout !== 'columns' && layout !== 'classic') return '';
  const col = crmebMenu.columnsNavList[crmebMenu.activeColumnIndex];
  return col?.title || '';
});

const asideWidthClass = computed(() => {
  const { layout, isCollapse } = themeStore.themeConfig;
  if (layout === 'columns') {
    return isCollapse ? 'layout-aside-width1' : 'layout-aside-width-default';
  }
  return isCollapse ? 'layout-aside-width64' : 'layout-aside-width-default';
});

/** 分栏子菜单才用 columns-sub-aside 样式；经典/默认用普通侧栏 */
const asideLayoutClass = computed(() => {
  const layout = themeStore.themeConfig.layout;
  if (layout === 'columns') return 'layout-columns-sub-aside layout-el-aside-br-color';
  if (layout === 'classic') return 'layout-el-aside-br-color';
  return '';
});

function onResize() {
  clientWidth.value = document.body.clientWidth;
}

onMounted(() => {
  window.addEventListener('resize', onResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', onResize);
});
</script>

<style scoped lang="scss">
.layout-aside-logo {
  flex-shrink: 0;
}
.cat-name {
  padding: 12px 14px;
  font-weight: 600;
  color: var(--prev-bg-menuBarColor, #303133);
  border-bottom: 1px solid var(--prev-border-color-lighter, #ebeef5);
}
.w100 {
  width: 100%;
}
.h100 {
  height: 100%;
}
.layout-aside {
  padding: 0 !important;
  .cat-name {
    text-align: center;
  }
}
.layout-aside-mobile-drawer {
  margin-bottom: 0 !important;
  background: var(--prev-bg-menuBar, #282c34) !important;
  display: flex;
  flex-direction: column;
  overflow-x: hidden !important;
}
</style>

<!-- drawer teleport 到 body，箭头/菜单样式需非 scoped -->
<style lang="scss">
.layout-aside-mobile-drawer-wrap {
  .el-drawer__body {
    padding: 0 !important;
    overflow: hidden;
    height: 100%;
    background: var(--prev-bg-menuBar, #282c34);
  }

  .layout-aside-mobile-drawer {
    /* 子菜单展开箭头（对齐 EB / 分栏侧栏箭头样式） */
    .el-sub-menu > .el-sub-menu__title {
      position: relative;
      padding-right: 36px !important;
      overflow: visible !important;
    }
    .el-sub-menu__icon-arrow {
      display: inline-flex !important;
      align-items: center;
      justify-content: center;
      visibility: visible !important;
      opacity: 1 !important;
      position: absolute !important;
      top: 50% !important;
      right: 12px !important;
      margin-top: -6px !important;
      margin-left: 0 !important;
      margin-right: 0 !important;
      width: 12px !important;
      height: 12px !important;
      font-size: 12px !important;
      color: var(--prev-bg-menuBarColor, #eaeaea) !important;
      svg {
        width: 1em;
        height: 1em;
      }
    }

    .el-menu-item,
    .el-sub-menu__title {
      color: var(--prev-bg-menuBarColor, #eaeaea) !important;
      background-color: transparent !important;
    }

    .el-menu-item:hover,
    .el-sub-menu__title:hover {
      color: var(--prev-MenuActiveColor, #fff) !important;
      background-color: var(--prev-bg-menu-hover-ba-color, #4073fa) !important;
    }

    /* 模具/移动端：只高亮二级叶子，一级不带选中底（对齐 EB） */
    .el-menu-item.is-active {
      color: #fff !important;
      background-color: var(--prev-bg-menu-hover-ba-color, #4073fa) !important;
    }

    .el-sub-menu.is-active > .el-sub-menu__title {
      color: var(--prev-bg-menuBarColor, #eaeaea) !important;
      background-color: transparent !important;
    }

    .el-sub-menu.is-active > .el-sub-menu__title i:not(.el-sub-menu__icon-arrow),
    .el-sub-menu.is-active > .el-sub-menu__title .crmeb-menu-icon {
      color: var(--prev-bg-menuBarColor, #eaeaea) !important;
    }
  }
}
</style>
